import { formatPaise } from '@tkm/money'

/**
 * Mitali's composer. Money is on every column header without a tap, because the
 * decision she is making is a margin decision and a board that hides it makes her
 * open cards to think.
 *
 * The move path on a folded phone is tap-card then tap-destination, not drag:
 * she works one-handed and drag is a two-hand operation (design system, 7).
 */
export interface DropCard {
  unitId: string
  title: string
  priceInPaise: number
  costInPaise: number
}

export interface DropColumn {
  id: string
  name: string
  channel: string
  cards: DropCard[]
}

export interface DropBoardProps {
  columns: DropColumn[]
  selectedUnitId?: string
}

function totals(cards: DropCard[]) {
  const price = cards.reduce((n, c) => n + c.priceInPaise, 0)
  const cost = cards.reduce((n, c) => n + c.costInPaise, 0)
  // Integer basis points, then one division for display only - never floating money.
  const marginBp = price === 0 ? 0 : Math.round(((price - cost) * 10000) / price)
  return { price, cost, marginPct: Math.round(marginBp / 100) }
}

export function DropBoard({ columns, selectedUnitId }: DropBoardProps) {
  const ordered = [...columns].sort((a, b) => (a.id === 'unallocated' ? -1 : b.id === 'unallocated' ? 1 : 0))
  const selectedColumn = ordered.find((c) => c.cards.some((card) => card.unitId === selectedUnitId))

  return (
    <div className="board">
      {ordered.map((column) => {
        const t = totals(column.cards)
        return (
          <section key={column.id} className="board-column">
            <header className="column-header">
              <span className="t-title">{column.name}</span>
              <span className="chip">{column.channel}</span>
              <span className="count-tick">{column.cards.length}</span>
              <span className="money">{formatPaise(t.price)}</span>
              <span className="money muted">{formatPaise(t.cost)}</span>
              <span className="money">{t.marginPct}%</span>
            </header>
            <ul className="column-cards">
              {column.cards.map((card) => (
                <li
                  key={card.unitId}
                  className={`board-card${card.unitId === selectedUnitId ? ' pulse-halo card-selected' : ''}`}
                >
                  <span>{card.title}</span>
                  <span className="money">{formatPaise(card.priceInPaise)}</span>
                </li>
              ))}
            </ul>
          </section>
        )
      })}

      {selectedUnitId ? (
        <div className="move-targets" role="group" aria-label="Move to">
          {ordered
            .filter((c) => c.id !== selectedColumn?.id)
            .map((c) => (
              <button key={c.id} type="button" className="chip move-target">
                {c.name}
              </button>
            ))}
        </div>
      ) : null}
    </div>
  )
}
